/**
 * Idea API contract types. Shared between apps/api and apps/web.
 *
 * The status and area vocabularies lived only in the web app, so the API
 * accepted any string for either and stored it. They are here so both sides
 * validate against one list.
 */

export const IDEA_FUNCTIONAL_AREAS = [
  "docs",
  "ai_agent",
  "flow_management",
  "portfolios",
  "risk_models",
  "pdf_templates",
  "clients",
  "settings",
  "other",
] as const;
export type IdeaFunctionalArea = (typeof IDEA_FUNCTIONAL_AREAS)[number];

export const IDEA_STATUSES = ["idea", "considering", "implementing", "done", "rejected"] as const;
export type IdeaStatus = (typeof IDEA_STATUSES)[number];

/** Column widths on `idea` / `idea_comment`. The API rejects anything longer. */
export const IDEA_LIMITS = {
  title: 30,
  description: 500,
  comment: 2000,
} as const;

type IdeaAuthor = {
  id: string;
  name: string;
  image: string | null;
};

/**
 * What `GET /internal/ideas` and `GET /internal/ideas/:id` actually return —
 * the single source of truth both `idea-service.ts`'s mappers and the web
 * panel type against, so the two sides cannot drift the way they did before
 * (a `type` field that was never in the `idea` table, `functionalArea`/
 * `status` typed as bare `string`).
 */
export type IdeaWithDetails = {
  id: string;
  title: string;
  description: string;
  imageUrl: string | null;
  functionalArea: IdeaFunctionalArea;
  status: IdeaStatus;
  voteCount: number;
  commentCount: number;
  createdById: string;
  createdBy: IdeaAuthor;
  /** ISO 8601 — serialized over the wire, never a `Date` instance. */
  createdAt: string;
  updatedAt: string;
  hasVoted: boolean;
};

export type IdeaComment = {
  id: string;
  ideaId: string;
  content: string;
  /** Set when the comment mirrors one in a source system. Null for QP-authored ones. */
  externalSyncId: string | null;
  createdById: string;
  createdBy: IdeaAuthor;
  createdAt: string;
  updatedAt: string;
};
